// Centralized SEO helper. Sets <title>, meta description/keywords,
// canonical, OpenGraph/Twitter tags, geo tags, and injects JSON-LD.

const ORIGIN = 'https://editingschoolbangalore.com';
const DEFAULT_OG_IMAGE =
  'https://images.unsplash.com/photo-1749410342681-3510f9edb7ad?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85';

const upsertMeta = (key, value, attr = 'name') => {
  if (!value) return;
  let tag = document.head.querySelector(`meta[${attr}="${key}"][data-seo]`);
  if (!tag) {
    tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  }
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    tag.setAttribute('data-seo', '1');
    document.head.appendChild(tag);
  } else {
    tag.setAttribute('data-seo', '1');
  }
  tag.setAttribute('content', value);
};

const upsertCanonical = (href) => {
  let tag = document.head.querySelector('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement('link');
    tag.rel = 'canonical';
    document.head.appendChild(tag);
  }
  tag.href = href;
};

export const setJsonLd = (id, data) => {
  if (!data) return;
  let tag = document.getElementById(id);
  if (!tag) {
    tag = document.createElement('script');
    tag.type = 'application/ld+json';
    tag.id = id;
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(data);
};

export const removeJsonLd = (id) => {
  const tag = document.getElementById(id);
  if (tag) tag.remove();
};

// path: '/' or '/courses/foo' (no domain, no hash)
// schemas: { 'course-jsonld': {...}, 'breadcrumb-jsonld': {...}, 'faq-jsonld': {...} }
export const applyPageMeta = ({
  title,
  description,
  keywords,
  path = '/',
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  schemas = {},
}) => {
  const url = `${ORIGIN}${path}`;
  if (title) document.title = title;
  upsertMeta('description', description);
  upsertMeta('keywords', keywords);
  upsertMeta('og:title', title, 'property');
  upsertMeta('og:description', description, 'property');
  upsertMeta('og:url', url, 'property');
  upsertMeta('og:type', ogType, 'property');
  upsertMeta('og:image', ogImage, 'property');
  upsertMeta('twitter:title', title, 'property');
  upsertMeta('twitter:description', description, 'property');
  upsertMeta('twitter:image', ogImage, 'property');
  upsertMeta('twitter:card', 'summary_large_image', 'property');
  // Geo tags (Koramangala, Bangalore — actual campus location)
  upsertMeta('geo.region', 'IN-KA');
  upsertMeta('geo.placename', 'Koramangala, Bangalore');
  upsertMeta('geo.position', '12.9352;77.6245');
  upsertMeta('ICBM', '12.9352, 77.6245');
  upsertCanonical(url);

  // Manage JSON-LD nodes
  Object.entries(schemas).forEach(([id, data]) => setJsonLd(id, data));
};

export const buildBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    name: it.name,
    item: `${ORIGIN}${it.path}`,
  })),
});

// Convert "14 months (includes 2-month internship)" etc. to ISO 8601 like "P14M"
export const toIso8601Duration = (txt) => {
  if (!txt) return undefined;
  const m = String(txt).match(/(\d+(?:\.\d+)?)\s*month/i);
  if (m) {
    const n = Math.round(parseFloat(m[1]));
    return `P${n}M`;
  }
  return undefined;
};

export const buildCourseSchema = (course) => ({
  '@context': 'https://schema.org',
  '@type': 'Course',
  name: course.title,
  description: course.seo?.description || course.description,
  provider: {
    '@type': 'EducationalOrganization',
    name: 'MatchFrame Editing School',
    sameAs: ORIGIN,
  },
  educationalCredentialAwarded: 'Certificate of Completion',
  inLanguage: course.languages,
  hasCourseInstance: {
    '@type': 'CourseInstance',
    courseMode: 'onsite',
    location: {
      '@type': 'Place',
      name: 'MatchFrame Editing School, Koramangala, Bangalore',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Unit 309, 3rd Floor, Workafella, Jyothi Nivas College Road, Koramangala Industrial Layout',
        addressLocality: 'Bangalore',
        addressRegion: 'KA',
        postalCode: '560095',
        addressCountry: 'IN',
      },
    },
    courseWorkload: toIso8601Duration(course.durationDetail || course.duration),
  },
});

export const buildFaqSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: f.answer,
    },
  })),
});
